import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo, CtaButton, MonoLabel } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const strongEnough = password.length >= 8 && /[a-zA-Z]/.test(password) && /\d/.test(password);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!strongEnough) {
      setError("At least 8 characters, with at least one letter and one number.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/auth"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen dot-grid flex flex-col" data-testid="reset-password-page">
      <div className="max-w-6xl w-full mx-auto px-6 py-6">
        <Link to="/"><Logo className="h-5 sm:h-6" /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-24">
        <div className="w-full max-w-md card-soft p-8 sm:p-10 fade-up">
          <MonoLabel>NEW PASSWORD</MonoLabel>
          {done ? (
            <div className="mt-5" data-testid="reset-password-success">
              <h1 className="text-2xl"><span className="font-serif-it">Done.</span></h1>
              <p className="mt-3 text-[14px] text-[#181621] bg-[#EEF6EF] rounded-lg px-4 py-3.5">
                Password updated. Your old sessions have been signed out. Taking you to login…
              </p>
            </div>
          ) : !token ? (
            <p className="mt-5 text-[14px] text-[#B4443C] bg-[#FBE9E7] rounded-lg px-4 py-3.5" data-testid="reset-missing-token">
              Reset link missing or malformed. <Link to="/forgot-password" className="underline font-semibold">Request a new one</Link>.
            </p>
          ) : (
            <>
              <h1 className="mt-5 text-2xl sm:text-3xl leading-tight">
                <span className="font-serif-it block">Choose a password</span>
                <span className="font-extrabold tracking-[-0.02em] block">you'll remember</span>
              </h1>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">New password</label>
                  <input className="field-input" type="password" placeholder="Min. 8 characters, letters and numbers" value={password}
                         onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password-input" />
                  {password && !strongEnough && (
                    <p className="text-[12px] text-[#B4443C] mt-1.5" data-testid="reset-strength-hint">At least 8 characters, with at least one letter and one number.</p>
                  )}
                </div>
                <div>
                  <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Confirm password</label>
                  <input className="field-input" type="password" placeholder="Repeat the password" value={confirm}
                         onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
                </div>
                {error && (
                  <p className="text-[13px] text-[#B4443C] bg-[#FBE9E7] rounded-lg px-3.5 py-2.5" data-testid="reset-error-message">{error}</p>
                )}
                <CtaButton type="submit" className="w-full mt-2" disabled={busy} data-testid="reset-submit-btn">
                  {busy ? "One moment…" : "Set the new password"}
                </CtaButton>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
